import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { useAppStore } from "@/store";

export default function Privacy() {
  const hasAccount = useAppStore((s) => s.hasAccount);
  const backPath = hasAccount ? "/settings" : "/login";
  const backLabel = hasAccount ? "Back to Settings" : "Back to Login";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto py-8 px-4"
    >
      <Link href={backPath}>
        <span className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer mb-6 block">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </span>
      </Link>

      <h1 className="text-3xl font-display font-bold mb-2">Privacy Policy</h1>
      <p className="text-sm text-muted-foreground mb-8">Last updated: March 13, 2026</p>

      <div className="prose prose-invert prose-sm max-w-none space-y-6 text-foreground/90">
        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">1. What We Collect</h2>
          <p>ScribPost collects the following personal data to provide our service:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Email address</strong> — used for account creation, login, and transactional emails (e.g. email verification).</li>
            <li><strong>Display name</strong> — optionally provided during onboarding for personalization.</li>
            <li><strong>Writing samples</strong> — text you paste or type to train your AI voice profile.</li>
            <li><strong>Audio recordings</strong> — voice notes you record are transcribed to text, then the raw audio is immediately discarded. We never store audio files.</li>
            <li><strong>Generated posts</strong> — the AI-generated content created from your ideas.</li>
            <li><strong>Ideas and brainstorms</strong> — notes and rough ideas you save to the Idea Vault.</li>
            <li><strong>Smart replies</strong> — original messages and generated replies you create.</li>
            <li><strong>Device fingerprint</strong> — a non-identifying hash used to link anonymous sessions to your account.</li>
            <li><strong>Payment information</strong> — handled entirely by Stripe. We store only your Stripe customer ID, not card details.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">2. How We Use Your Data</h2>
          <ul className="list-disc pl-6 space-y-1">
            <li>To provide and improve the ScribPost service (generating posts in your voice).</li>
            <li>To process payments and manage subscriptions via Stripe.</li>
            <li>To send transactional emails (verification, password reset) via Resend.</li>
            <li>To analyze your writing style using OpenAI's API (your data is sent to OpenAI for processing but is not used to train their models under our API agreement).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">3. Data Storage</h2>
          <p>Your data is stored in a PostgreSQL database hosted on Replit's infrastructure. All data is encrypted in transit (TLS) and at rest. Passwords are hashed using bcrypt with a cost factor of 12.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">4. Third-Party Processors</h2>
          <p>We share your data with the following third-party services only as needed to operate ScribPost:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>OpenAI</strong> — processes your writing samples and ideas to generate content. Subject to OpenAI's API data usage policy.</li>
            <li><strong>Stripe</strong> — processes payments and manages subscriptions. Subject to Stripe's privacy policy.</li>
            <li><strong>Resend</strong> — sends transactional emails. Subject to Resend's privacy policy.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">5. Your Rights</h2>
          <p>Under GDPR, UK GDPR, and CCPA, you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Access</strong> — download all your data at any time from Settings using "Download My Data."</li>
            <li><strong>Deletion</strong> — permanently delete your account and all associated data from Settings using "Delete My Account." This is a hard delete with no recovery.</li>
            <li><strong>Portability</strong> — export your data as a JSON file.</li>
            <li><strong>Rectification</strong> — update your profile information through the app.</li>
            <li><strong>Object</strong> — contact us to object to specific data processing.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">6. Data Retention</h2>
          <p>We retain your data for as long as your account is active. When you delete your account, all data is permanently removed from our database immediately. Audio recordings are never stored — they are transcribed in-memory and discarded.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">7. Cookies</h2>
          <p>ScribPost uses only essential local storage to remember your session and preferences. We do not use third-party tracking cookies or analytics services.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">8. Children's Privacy</h2>
          <p>ScribPost is not intended for users under 16 years of age. We do not knowingly collect data from children.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">9. Changes to This Policy</h2>
          <p>We may update this policy from time to time. Material changes will be communicated via email or an in-app notice. Continued use of ScribPost after changes constitutes acceptance.</p>
        </section>

        <section>
          <h2 className="text-xl font-display font-semibold text-foreground">10. Contact</h2>
          <p>For privacy-related questions or to exercise your rights, contact us at <a href="mailto:privacy@scribpost.com" className="text-primary hover:underline">privacy@scribpost.com</a>.</p>
        </section>
      </div>
    </motion.div>
  );
}
